/**
 * Share — `UIActivityViewController` on iOS / `ACTION_SEND` on Android via
 * `@capacitor/share`, falling back to the Web Share API (or clipboard) on web.
 *
 * Why a wrapper over plain `navigator.share`: the Web Share API technically
 * exists in iOS WKWebView from 12.2+, but its user-activation requirement
 * collides with Capacitor's plugin bridge. Taps that travel through the
 * bridge sometimes lose their "user-initiated" stamp and `navigator.share`
 * silently no-ops or throws an indistinguishable AbortError. Routing through
 * the Capacitor Share plugin uses iOS's native share controller directly,
 * which doesn't have that constraint.
 *
 * Return values:
 *   { ok: true,  shared: true  } — user actually shared somewhere
 *   { ok: true,  shared: false } — user opened the sheet but cancelled
 *   { ok: false, reason: '...' } — plugin/API unavailable or threw
 *
 * Callers typically only care about `ok` — if it's true, they record the
 * "user engaged with share" signal regardless of which target they picked.
 */

import { isNative } from '../runtime/index.ts';

export interface ShareInput {
  /** Plain-text body. Some targets (Twitter, Messages) only show this. */
  text: string;
  /** URL to share. Most targets show both text and URL. */
  url?: string;
  /** Title — shown in the iOS share sheet header and used by Mail as subject. */
  title?: string;
  /** iOS-only: dialog hint above the activity sheet (rarely visible). */
  dialogTitle?: string;
}

export type ShareResult =
  | { ok: true; shared: boolean }
  | { ok: false; reason: string };

export async function share(input: ShareInput): Promise<ShareResult> {
  if (isNative()) {
    try {
      const { Share } = await import('@capacitor/share');
      const can = await Share.canShare();
      if (!can.value) return { ok: false, reason: 'share-unavailable' };
      const res = await Share.share({
        title: input.title,
        text: input.text,
        url: input.url,
        dialogTitle: input.dialogTitle ?? input.title,
      });
      // Capacitor returns activityType when the user picked a target; empty
      // string / undefined when they dismissed the sheet without choosing.
      const shared = !!res.activityType;
      return { ok: true, shared };
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return { ok: false, reason: msg };
    }
  }

  // Web fallback — Web Share API where supported (Safari, Chrome Android),
  // otherwise the caller should clipboard-copy. We surface `unavailable`
  // so the caller knows to fall back rather than treating it as an error.
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title: input.title, text: input.text, url: input.url });
      return { ok: true, shared: true };
    } catch (err) {
      if ((err as DOMException)?.name === 'AbortError') {
        return { ok: true, shared: false };
      }
      const msg = err instanceof Error ? err.message : String(err);
      return { ok: false, reason: msg };
    }
  }
  return { ok: false, reason: 'unavailable' };
}
